import { Component } from '@theme/component';
import { debounce, announce, t, tCount } from '@theme/utilities';

/**
 * Live search results as the shopper types.
 *
 * Wraps a real search form, so submitting still lands on `/search` and the
 * suggestions are an enhancement rather than the only route to results.
 *
 * The results list is rendered by Shopify's predictive search endpoint using
 * `snippets/predictive-search-results.liquid`, which means the suggestion
 * markup and the search page markup come from the same Liquid.
 *
 * @extends {Component}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['input', 'results'];

  /** @type {AbortController | undefined} */
  #pending;

  /** @type {Map<string, string>} */
  #cache = new Map();

  search = debounce(() => this.#search(), 250);

  async #search() {
    const input = /** @type {HTMLInputElement} */ (this.refs.input);
    const results = /** @type {HTMLElement} */ (this.refs.results);
    const term = input.value.trim();

    this.#pending?.abort();

    if (term.length < 2) {
      results.innerHTML = '';
      this.removeAttribute('open');
      return;
    }

    const cached = this.#cache.get(term);
    if (cached != null) {
      this.#present(cached);
      return;
    }

    const url = new URL(window.Theme?.routes?.predictive_search_url ?? '/search/suggest', window.location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('resources[type]', 'product,page,article');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('section_id', 'predictive-search');

    this.#pending = new AbortController();
    this.setAttribute('loading', '');

    try {
      const response = await fetch(url, { signal: this.#pending.signal });
      const html = await response.text();
      // The section response is a full document fragment; only the results
      // list inside it is wanted.
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const markup = parsed.querySelector('[data-predictive-results]')?.innerHTML ?? '';

      this.#cache.set(term, markup);
      this.#present(markup);
    } catch (error) {
      if (/** @type {Error} */ (error).name !== 'AbortError') console.warn('[predictive-search]', error);
    } finally {
      this.removeAttribute('loading');
    }
  }

  /**
   * @param {string} markup
   */
  #present(markup) {
    const results = /** @type {HTMLElement} */ (this.refs.results);
    results.innerHTML = markup;
    this.toggleAttribute('open', markup.trim() !== '');

    const count = results.querySelectorAll('[data-search-result]').length;
    announce(count ? tCount('results_count', count) : t('no_results'));
  }

  /**
   * Arrow keys move through the suggestions without leaving the input, so the
   * shopper can keep typing. Escape clears rather than closing the whole
   * search, which is the more common intent.
   *
   * @param {KeyboardEvent} event
   */
  navigate = (event) => {
    if (event.key === 'Escape') {
      this.removeAttribute('open');
      return;
    }

    const step = event.key === 'ArrowDown' ? 1 : event.key === 'ArrowUp' ? -1 : 0;
    if (!step) return;

    event.preventDefault();

    const links = Array.from(this.querySelectorAll('[data-search-result] a'));
    if (!links.length) return;

    const current = links.findIndex((link) => link === document.activeElement);
    const next = links[(current + step + links.length) % links.length];
    if (next instanceof HTMLElement) next.focus();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
